import { useEffect, useState } from "react";
import { Check, FolderPlus, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useWorld } from "@/context/WorldContext";
import { api, formatApiError } from "@/lib/api";
import { collectionsOf } from "@/lib/collections";

export const CollectionNameDialog = ({ open, setOpen, title, initial = "", onSave }) => {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(initial); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try { await onSave(name.trim()); setOpen(false); } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-panel border-line max-w-sm" data-testid="collection-name-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-50">{title}</DialogTitle>
            <DialogDescription>For example "House Stark" or "Villains".</DialogDescription>
          </DialogHeader>
          <Input autoFocus required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Collection name" data-testid="collection-name-input" />
          <DialogFooter>
            <Button type="submit" disabled={busy || !name.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="collection-name-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const NewCollectionButton = ({ category, className = "" }) => {
  const { world, reload } = useWorld();
  const [open, setOpen] = useState(false);
  const save = async (name) => { await api.post(`/worlds/${world.id}/collections`, { category, name }); await reload(); toast.success(`Collection "${name}" created`); };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="new-collection-btn"
        className={`inline-flex items-center gap-1.5 rounded-full border border-line text-sm text-slate-300 px-4 py-2.5 hover:border-amber-500/40 hover:text-amber-300 transition-colors ${className}`}>
        <FolderPlus className="w-4 h-4" /> New collection
      </button>
      <CollectionNameDialog open={open} setOpen={setOpen} title={`New ${category.toLowerCase()} collection`} onSave={save} />
    </>
  );
};

export const CollectionMenu = ({ col }) => {
  const { reload } = useWorld();
  const [rename, setRename] = useState(false);
  const [del, setDel] = useState(false);
  const save = async (name) => { await api.put(`/collections/${col.id}`, { name }); await reload(); };
  const remove = async () => {
    try { await api.delete(`/collections/${col.id}`); await reload(); toast.success("Collection deleted. Its entries are safe."); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-md text-slate-500 hover:text-slate-100 hover:bg-veil/[0.05]" data-testid={`collection-menu-${col.id}`}><MoreHorizontal className="w-4 h-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-panel border-line">
          <DropdownMenuItem onSelect={() => setRename(true)} data-testid={`collection-rename-${col.id}`}><Pencil className="w-3.5 h-3.5 mr-2" /> Rename</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDel(true)} className="text-rose-300" data-testid={`collection-delete-${col.id}`}><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CollectionNameDialog open={rename} setOpen={setRename} title="Rename collection" initial={col.name} onSave={save} />
      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent className="bg-panel border-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete "{col.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Only the collection goes. Its entries stay in the codex.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-500" data-testid="collection-delete-confirm">Delete collection</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const CollectionPicker = ({ category, value, onChange }) => {
  const { world, collections, reload } = useWorld();
  const [open, setOpen] = useState(false);
  const cols = collectionsOf(collections, category);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const create = async (name) => {
    const { data } = await api.post(`/worlds/${world.id}/collections`, { category, name });
    await reload();
    onChange([...value, data.id]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="collection-picker">
      {cols.map((c) => {
        const on = value.includes(c.id);
        return (
          <button key={c.id} type="button" onClick={() => toggle(c.id)} aria-pressed={on} data-testid={`collection-option-${c.id}`}
            className={`inline-flex items-center gap-1 text-xs rounded-full border px-3 py-1 transition-colors ${on ? "bg-amber-500/15 border-amber-500/50 text-amber-200" : "border-line text-slate-400 hover:text-slate-100"}`}>
            {on && <Check className="w-3 h-3" />}{c.name}
          </button>
        );
      })}
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs rounded-full border border-dashed border-line px-3 py-1 text-slate-500 hover:text-amber-300 hover:border-amber-500/40 transition-colors" data-testid="collection-picker-new">
        <Plus className="w-3 h-3" /> New
      </button>
      <CollectionNameDialog open={open} setOpen={setOpen} title={`New ${category.toLowerCase()} collection`} onSave={create} />
    </div>
  );
};
